/**
 * Curated Articles Loader
 * 
 * @description Fetches and renders curated articles from Shopify GraphQL API.
 * Dynamically loads articles based on configured handles and initializes
 * the corresponding swiper carousel.
 * 
 * @features
 * - GraphQL API integration with Shopify Storefront
 * - Dynamic article filtering by handles
 * - Automatic swiper initialization
 * - Responsive grid fallback for ≤3 articles
 * - Error handling and fallback states
 * 
 * @dependencies ArticlesSwiper, Shopify Storefront API
 * @version 1.0.0
 */

(function() {
  document.addEventListener('DOMContentLoaded', function() {

    // Get DOM elements needed for the curated articles functionality
    const section = document.querySelector('.curated-articles-section');
    const container = document.getElementById('articles-container');

    // Early return if required elements are not found
    if (!section || !container) {
      return;
    }
    
    // Extract configuration data from section data attributes
    const blogDatabaseId = section.dataset.blogDatabaseId;
    const accessToken = section.dataset.accessToken;
    const apiUrl = section.dataset.apiUrl;
    const targetHandlesRaw = section.dataset.targetHandles;
    
    // Validate that all required API configuration is available
    if (!accessToken || !apiUrl || !targetHandlesRaw) {
      return;
    }
    
    // Convert comma-separated string to clean array of handles
    const targetHandles = targetHandlesRaw.split(',').map(handle => handle.trim());
    searchAllArticles();
    
    // Main function to fetch articles from Shopify GraphQL API
    async function searchAllArticles() {
      try {
        // Encode blog ID for GraphQL query
        const blogId = btoa(`gid://shopify/Blog/${blogDatabaseId}`);
        
        // GraphQL query to fetch all articles from the specified blog
        const query = `{
          blog(id: "${blogId}") {
            articles(first: 250, sortKey: PUBLISHED_AT, reverse: true) {
              edges {
                node {
                  handle
                  title
                  onlineStoreUrl
                  excerpt
                  image {
                    originalSrc
                    altText
                  }
                }
              }
            }
          }
        }`;
        
        // Make API request to Shopify Storefront API
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({ query: query })
        });
        
        // Validate response status before processing
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Process API response and filter articles
        if (data.data && data.data.blog && data.data.blog.articles) {
          const allArticles = data.data.blog.articles.edges.map(edge => edge.node);
          const matchedArticles = [];
          
          // Filter articles based on target handles
          targetHandles.forEach(targetHandle => {
            // Handle different URL formats (with/without 'learn/' prefix)
            const cleanTarget = targetHandle.replace('learn/', '');
            const article = allArticles.find(a => 
              a.handle === targetHandle || 
              a.handle === cleanTarget ||
              a.handle === `learn/${cleanTarget}`
            );
            if (article) {
              matchedArticles.push(article);
            }
          });
          renderArticles(matchedArticles);
        }
      } catch (error) {
        container.innerHTML = '<div class="swiper-slide"><p>Unable to load articles. Please try again later.</p></div>';
        initializeSwiper();
      }
    }
    
    // Render filtered articles in the DOM
    function renderArticles(articles) {
      // Show fallback message if no articles found
      if (articles.length === 0) {
        container.innerHTML = '<div class="swiper-slide"><p>No articles found</p></div>';
        initializeSwiper();
        return;
      }
      
      // Clear container and rebuild with new articles
      container.innerHTML = '';
      articles.forEach((article, index) => {
        const articleEl = document.createElement('div');
        articleEl.className = 'swiper-slide';
        
        // Build article HTML with conditional image display
        articleEl.innerHTML = `
          <article class="article__card">
            ${article.image && article.image.originalSrc ? `
              <a href="${article.onlineStoreUrl}">
                <img src="${article.image.originalSrc}?width=800&height=450&crop=center" alt="${article.image.altText || article.title}" loading="lazy">
              </a>
            ` : ''}
            <h3>
              <a href="${article.onlineStoreUrl}">${article.title}</a>
            </h3>
            <a href="${article.onlineStoreUrl}" class="article__card-button">Read More</a>
          </article>
        `;
        container.appendChild(articleEl);
      });
      
      // Add class for 3 or fewer articles
      if (articles.length <= 3) {
        const swiperContainer = container.closest('.swiper-carousel');
        swiperContainer.classList.add('articles-desktop-grid');
      }
      // Initialize Swiper after articles are loaded
      initializeSwiper();
    }
    
    // Function to properly initialize Swiper with correct timing
    function initializeSwiper() {
      setTimeout(() => {
        if (window.ArticlesSwiper) {
          const swipers = window.ArticlesSwiper.init();
        }
      }, 100);
    }
  });
})();